import React from 'react'
import { Form, InputNumber, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import type {
    ValidationCategoryWeights,
    ValidationDecisionStandards,
    ValidationMetricWeights,
    ValidationRuleConfig,
} from '../../../types/modelValidation'
import {
    basisPointsToPercent,
    formatBasisPoints,
    percentToBasisPoints,
} from './modelValidationUtils'
import './ValidationRuleEditor.css'

interface ValidationRuleEditorProps {
    disabled?: boolean
    onChange: (config: ValidationRuleConfig) => void
    value: ValidationRuleConfig
}

const categoryWeightFields: Array<keyof ValidationCategoryWeights> = [
    'aedes',
    'culex',
    'otherMosquito',
]

const metricWeightFields: Array<keyof ValidationMetricWeights> = [
    'recognition',
    'error_control',
    'miss_control',
]

const standardFields: Array<keyof ValidationDecisionStandards> = [
    'min_composite_score_bp',
    'min_composite_improvement_bp',
    'max_weighted_error_rate_bp',
    'max_weighted_miss_rate_bp',
]

const ValidationRuleEditor: React.FC<ValidationRuleEditorProps> = ({
    disabled = false,
    onChange,
    value,
}) => {
    const { t } = useTranslation(['admin'])

    const renderPercentageInput = (
        ariaLabel: string,
        currentValue: number,
        updateValue: (nextValue: number) => void,
    ) => (
        <InputNumber
            addonAfter="%"
            aria-label={ariaLabel}
            disabled={disabled}
            max={100}
            min={0}
            onChange={(nextValue) =>
                updateValue(percentToBasisPoints(nextValue ?? 0))
            }
            precision={2}
            style={{ width: '100%' }}
            value={basisPointsToPercent(currentValue)}
        />
    )

    const renderWeightTotal = (total: number) => {
        const valid = total === 10000
        return (
            <Typography.Text
                aria-live="polite"
                className={`validation-rule-weight-total${valid ? '' : ' validation-rule-weight-total--invalid'}`}
                type={valid ? 'secondary' : 'danger'}
            >
                {t('admin:modelValidation.rules.weightTotal', {
                    total: formatBasisPoints(total),
                })}
                {!valid
                    ? ` · ${t('admin:modelValidation.rules.weightTotalError')}`
                    : ''}
            </Typography.Text>
        )
    }

    const categoryWeightTotal = Object.values(value.category_weights).reduce(
        (total, weight) => total + weight,
        0,
    )
    const metricWeightTotal = Object.values(value.metric_weights).reduce(
        (total, weight) => total + weight,
        0,
    )

    return (
        <Form
            className="validation-rule-editor"
            component="div"
            layout="vertical"
        >
            <section
                aria-label={t('admin:modelValidation.rules.categoryWeights')}
                className="validation-rule-section"
            >
                <div className="validation-rule-section-header">
                    <div>
                        <Typography.Title level={5} style={{ margin: 0 }}>
                            {t('admin:modelValidation.rules.categoryWeights')}
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            {t(
                                'admin:modelValidation.rules.categoryWeightsHint',
                            )}
                        </Typography.Text>
                    </div>
                    {renderWeightTotal(categoryWeightTotal)}
                </div>
                <div className="validation-rule-section-body validation-rule-field-grid validation-rule-field-grid--three">
                    {categoryWeightFields.map((field) => {
                        const label = t(
                            `admin:modelValidation.rules.categoryWeightFields.${field}`,
                        )
                        return (
                            <Form.Item key={field} label={label}>
                                {renderPercentageInput(
                                    label,
                                    value.category_weights[field],
                                    (nextValue) =>
                                        onChange({
                                            ...value,
                                            category_weights: {
                                                ...value.category_weights,
                                                [field]: nextValue,
                                            },
                                        }),
                                )}
                            </Form.Item>
                        )
                    })}
                </div>
            </section>

            <section
                aria-label={t('admin:modelValidation.rules.metricWeights')}
                className="validation-rule-section"
            >
                <div className="validation-rule-section-header">
                    <div>
                        <Typography.Title level={5} style={{ margin: 0 }}>
                            {t('admin:modelValidation.rules.metricWeights')}
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            {t(
                                'admin:modelValidation.rules.metricWeightsHint',
                            )}
                        </Typography.Text>
                    </div>
                    {renderWeightTotal(metricWeightTotal)}
                </div>
                <div className="validation-rule-section-body validation-rule-field-grid validation-rule-field-grid--three">
                    {metricWeightFields.map((field) => {
                        const label = t(
                            `admin:modelValidation.rules.metricWeightFields.${field}`,
                        )
                        return (
                            <Form.Item key={field} label={label}>
                                {renderPercentageInput(
                                    label,
                                    value.metric_weights[field],
                                    (nextValue) =>
                                        onChange({
                                            ...value,
                                            metric_weights: {
                                                ...value.metric_weights,
                                                [field]: nextValue,
                                            },
                                        }),
                                )}
                            </Form.Item>
                        )
                    })}
                </div>
            </section>

            <section
                aria-label={t('admin:modelValidation.rules.releaseStandards')}
                className="validation-rule-section"
            >
                <div className="validation-rule-section-header">
                    <div>
                        <Typography.Title level={5} style={{ margin: 0 }}>
                            {t('admin:modelValidation.rules.releaseStandards')}
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            {t(
                                'admin:modelValidation.rules.releaseStandardsHint',
                            )}
                        </Typography.Text>
                    </div>
                </div>
                <div className="validation-rule-section-body validation-rule-field-grid">
                    {standardFields.map((field) => {
                        const label = t(
                            `admin:modelValidation.rules.standardFields.${field}`,
                        )
                        return (
                            <Form.Item key={field} label={label}>
                                {renderPercentageInput(
                                    label,
                                    value.standards[field],
                                    (nextValue) =>
                                        onChange({
                                            ...value,
                                            standards: {
                                                ...value.standards,
                                                [field]: nextValue,
                                            },
                                        }),
                                )}
                            </Form.Item>
                        )
                    })}
                </div>
            </section>
        </Form>
    )
}

export default ValidationRuleEditor
